// ========================================
// components/admin/IntegrityAlerts.js - Admin Integrity Dashboard
// ========================================
// FILE PATH: frontend/src/components/admin/IntegrityAlerts.js
// PURPOSE: Admin interface for viewing and managing integrity alerts
// Shows tampering alerts, allows investigation, resolution, dismissal, and restoration
// Features:
//   - View open and resolved alerts
//   - Investigate tampering details
//   - Restore assets from original snapshot
//   - Compare original vs tampered values
//   - Add investigation notes
//   - Mark alerts as resolved or dismissed
// ========================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const IntegrityAlerts = () => {
  // ========================================
  // STATE VARIABLES
  // ========================================
  const [openAlerts, setOpenAlerts] = useState([]);           // Open/investigating alerts
  const [resolvedAlerts, setResolvedAlerts] = useState([]);   // Resolved/dismissed alerts
  const [loading, setLoading] = useState(true);               // Loading state
  const [selectedAlert, setSelectedAlert] = useState(null);   // Currently selected alert
  const [showModal, setShowModal] = useState(false);          // Modal visibility
  const [actionNotes, setActionNotes] = useState('');         // Notes for actions
  const [counts, setCounts] = useState({ open: 0, total: 0 }); // Alert counts
  const [activeTab, setActiveTab] = useState('open');         // Current tab: 'open' or 'resolved'
  const [actionLoading, setActionLoading] = useState(false);  // Loading state for actions

  // ========================================
  // FETCH ALL ALERT DATA ON MOUNT
  // ========================================
  useEffect(() => {
    fetchData();
  }, []);

  // ========================================
  // FETCH ALERTS FROM API
  // ========================================
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      
      const [openRes, resolvedRes, countRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/integrity/alerts/open`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/integrity/alerts/resolved`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/integrity/count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      setOpenAlerts(openRes.data.alerts);
      setResolvedAlerts(resolvedRes.data.alerts);
      setCounts(countRes.data);
    } catch (err) {
      console.error('Failed to fetch integrity alerts:', err);
      if (err.response?.status === 401) {
        window.location.href = '/admin/login';
      }
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // UPDATE ALERT STATUS (Resolve/Dismiss)
  // ========================================
  const handleStatusUpdate = async (alertId, status) => {
    if (!window.confirm(`Are you sure you want to mark this alert as ${status}?`)) {
      return;
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${process.env.REACT_APP_API_URL}/api/integrity/alerts/${alertId}/status`,
        { status, notes: actionNotes },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setShowModal(false);
      setActionNotes('');
      fetchData();
    } catch (err) {
      alert('Failed to update alert status');
    } finally {
      setActionLoading(false);
    }
  };
  
  // ========================================
  // RESTORE ASSET FROM ORIGINAL SNAPSHOT
  // ========================================
  // Creates a new asset with the original data
  // The tampered version is archived for audit
  // ========================================
  const handleRestore = async (alertId) => {
    if (!window.confirm('Are you sure you want to restore this asset to its original state?\n\nThis will create a new asset with the original data. The tampered version will be archived.')) {
      return;
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/integrity/alerts/${alertId}/restore`,
        { notes: actionNotes || 'Restored by admin' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      alert(`Asset restored successfully!\nNew asset created with ID: ${response.data.restoredAsset.id}`);
      setShowModal(false);
      setActionNotes('');
      fetchData();
    } catch (err) {
      alert('Failed to restore asset: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setActionLoading(false);
    }
  };

  // ========================================
  // VIEW ALERT DETAILS IN MODAL
  // ========================================
  const viewAlertDetails = async (alertId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/integrity/alerts/${alertId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedAlert(response.data);
      setShowModal(true);
      setActionNotes('');
    } catch (err) {
      alert('Failed to load alert details');
    }
  };

  // ========================================
  // FORMAT DATE FOR DISPLAY
  // ========================================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ========================================
  // RENDER LOADING STATE
  // ========================================
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading integrity alerts...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* ======================================== */}
      {/* HEADER */}
      {/* ======================================== */}
      <div style={styles.header}>
        <h2 style={styles.title}>🛡️ System Integrity</h2>
        {counts.open > 0 && (
          <span style={styles.badge}>{counts.open} Open Alerts</span>
        )}
      </div>

      {/* ======================================== */}
      {/* TABS */}
      {/* ======================================== */}
      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('open')}
          style={{...styles.tab, ...(activeTab === 'open' ? styles.activeTab : {})}}
        >
          Open Alerts ({openAlerts.length})
        </button>
        <button
          onClick={() => setActiveTab('resolved')}
          style={{...styles.tab, ...(activeTab === 'resolved' ? styles.activeTab : {})}}
        >
          Resolved ({resolvedAlerts.length})
        </button>
      </div>

      {/* ======================================== */}
      {/* OPEN ALERTS LIST */}
      {/* ======================================== */}
      {activeTab === 'open' && (
        <div style={styles.alertsList}>
          {openAlerts.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>✓</div>
              <h3 style={styles.emptyTitle}>No Open Alerts</h3>
              <p style={styles.emptyText}>All assets are verified. No tampering detected.</p>
            </div>
          ) : (
            openAlerts.map(alert => (
              <div key={alert._id} style={styles.alertCard}>
                <div style={styles.alertHeader}>
                  <span style={styles.alertIcon}>🚨</span>
                  <span style={styles.alertStatus}>{alert.status}</span>
                  <span style={styles.alertDate}>
                    {formatDate(alert.detectedAt)}
                  </span>
                </div>
                <div style={styles.alertContent}>
                  <p><strong>Asset:</strong> {alert.assetSnapshot?.assetName || 'Unknown'}</p>
                  <p><strong>Deceased:</strong> {alert.deceasedUserName}</p>
                  <p><strong>Tampered Field:</strong> <code style={styles.code}>{alert.fieldTampered}</code></p>
                  <div style={styles.valueCompare}>
                    <span style={styles.originalValue}>Original: {String(alert.originalValue)}</span>
                    <span style={styles.arrow}>→</span>
                    <span style={styles.tamperedValue}>Tampered: {String(alert.tamperedValue)}</span>
                  </div>
                </div>
                <div style={styles.alertActions}>
                  <button
                    onClick={() => viewAlertDetails(alert._id)}
                    style={styles.detailsButton}
                  >
                    Investigate
                  </button>
                  <button
                    onClick={() => handleRestore(alert._id)}
                    style={styles.restoreButton}
                    disabled={actionLoading}
                  >
                    🔄 Restore Original
                  </button>
                  <button
                    onClick={() => handleStatusUpdate(alert._id, 'resolved')}
                    style={styles.resolveButton}
                  >
                    Resolve
                  </button>
                  <button
                    onClick={() => handleStatusUpdate(alert._id, 'dismissed')}
                    style={styles.dismissButton}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ======================================== */}
      {/* RESOLVED ALERTS LIST */}
      {/* ======================================== */}
      {activeTab === 'resolved' && (
        <div style={styles.alertsList}>
          {resolvedAlerts.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No resolved alerts found.</p>
            </div>
          ) : (
            resolvedAlerts.map(alert => (
              <div key={alert._id} style={{...styles.alertCard, ...styles.resolvedCard}}>
                <div style={styles.alertHeader}>
                  <span style={styles.alertIcon}>✅</span>
                  <span style={styles.alertStatus}>{alert.status}</span>
                  <span style={styles.alertDate}>
                    Resolved: {alert.resolvedAt ? formatDate(alert.resolvedAt) : 'N/A'}
                  </span>
                </div>
                <div style={styles.alertContent}>
                  <p><strong>Asset:</strong> {alert.assetSnapshot?.assetName || 'Unknown'}</p>
                  <p><strong>Deceased:</strong> {alert.deceasedUserName}</p>
                  <p><strong>Resolution:</strong> {alert.resolutionNotes || 'No notes'}</p>
                  <p><strong>Resolved by:</strong> {alert.resolvedByEmail}</p>
                </div>
                <div style={styles.alertActions}>
                  <button
                    onClick={() => viewAlertDetails(alert._id)}
                    style={styles.detailsButton}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ======================================== */}
      {/* ALERT DETAILS MODAL */}
      {/* ======================================== */}
      {showModal && selectedAlert && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalClose} onClick={() => setShowModal(false)}>×</button>
            <h3 style={styles.modalTitle}>Integrity Alert Investigation</h3>
            
            {/* Asset Information */}
            <div style={styles.modalSection}>
              <h4>Asset Information</h4>
              <p><strong>Asset Name:</strong> {selectedAlert.asset?.assetName}</p>
              <p><strong>Asset Type:</strong> {selectedAlert.asset?.assetType}</p>
              <p><strong>Deceased:</strong> {selectedAlert.alert?.deceasedUserName}</p>
              <p><strong>Detected:</strong> {formatDate(selectedAlert.alert?.detectedAt)}</p>
              <p><strong>Certificate:</strong> {selectedAlert.asset?.certificateNumber}</p>
            </div>

            {/* Original vs Tampered Comparison */}
            <div style={styles.modalSection}>
              <h4>Tampering Comparison</h4>
              <div style={styles.comparisonGrid}>
                <div style={styles.comparisonColumn}>
                  <h5>Original Value</h5>
                  <code style={styles.originalCode}>{JSON.stringify(selectedAlert.alert?.originalValue)}</code>
                </div>
                <div style={styles.comparisonColumn}>
                  <h5>Tampered Value</h5>
                  <code style={styles.tamperedCode}>{JSON.stringify(selectedAlert.alert?.tamperedValue)}</code>
                </div>
              </div>
              <p style={styles.comparisonNote}>
                <strong>Field Tampered:</strong> <code>{selectedAlert.alert?.fieldTampered}</code>
              </p>
            </div>

            {/* Admin Notes */}
            <div style={styles.modalSection}>
              <h4>Admin Notes</h4>
              <textarea
                placeholder="Add investigation notes or resolution details..."
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                style={styles.textarea}
                rows="4"
              />
            </div>

            {/* Previous Notes */}
            {selectedAlert.alert?.adminNotes?.length > 0 && (
              <div style={styles.modalSection}>
                <h4>Previous Notes</h4>
                {selectedAlert.alert.adminNotes.map((note, idx) => (
                  <div key={idx} style={styles.noteItem}>
                    <p style={styles.noteText}>{note.note}</p>
                    <p style={styles.noteMeta}>- {note.addedByEmail}, {formatDate(note.addedAt)}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div style={styles.modalActions}>
              <button
                onClick={() => handleRestore(selectedAlert.alert?._id)}
                style={styles.modalRestoreButton}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : '🔄 Restore Original Asset'}
              </button>
              <button
                onClick={() => handleStatusUpdate(selectedAlert.alert?._id, 'investigating')}
                style={styles.modalInvestigateButton}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Mark Investigating'}
              </button>
              <button
                onClick={() => handleStatusUpdate(selectedAlert.alert?._id, 'resolved')}
                style={styles.modalResolveButton}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Mark Resolved'}
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={styles.modalCancelButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================
// STYLES
// ========================================
const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#F5F0E6',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    color: '#1A1A1A',
    margin: 0,
  },
  badge: {
    backgroundColor: '#dc2626',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: '1px solid #ddd',
    paddingBottom: '10px',
  },
  tab: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    borderRadius: '5px',
  },
  activeTab: {
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    fontWeight: 'bold',
  },
  alertsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  alertCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderLeft: '4px solid #dc2626',
  },
  resolvedCard: {
    borderLeft: '4px solid #10b981',
  },
  alertHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '15px',
  },
  alertIcon: {
    fontSize: '20px',
  },
  alertStatus: {
    padding: '2px 8px',
    borderRadius: '3px',
    fontSize: '12px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    textTransform: 'capitalize',
  },
  alertDate: {
    fontSize: '12px',
    color: '#666',
    marginLeft: 'auto',
  },
  alertContent: {
    marginBottom: '15px',
  },
  valueCompare: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#F5F0E6',
    borderRadius: '5px',
    fontSize: '13px',
  },
  originalValue: {
    color: '#2E7D32',
  },
  tamperedValue: {
    color: '#dc2626',
  },
  arrow: {
    color: '#666',
  },
  code: {
    backgroundColor: '#f0f0f0',
    padding: '2px 6px',
    borderRadius: '3px',
    fontFamily: 'monospace',
    fontSize: '12px',
  },
  alertActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  detailsButton: {
    padding: '6px 12px',
    backgroundColor: 'transparent',
    border: '1px solid #2E7D32',
    color: '#2E7D32',
    borderRadius: '3px',
    cursor: 'pointer',
  },
  restoreButton: {
    padding: '6px 12px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  resolveButton: {
    padding: '6px 12px',
    backgroundColor: '#2E7D32',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
  },
  dismissButton: {
    padding: '6px 12px',
    backgroundColor: '#666',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px',
    backgroundColor: 'white',
    borderRadius: '10px',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '20px',
    color: '#1A1A1A',
    marginBottom: '10px',
  },
  emptyText: {
    color: '#666',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '60px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #F5F0E6',
    borderTop: '4px solid #FFD700',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '30px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    position: 'relative',
  },
  modalClose: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
  },
  modalTitle: {
    fontSize: '20px',
    color: '#FFD700',
    marginBottom: '20px',
  },
  modalSection: {
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '1px solid #eee',
  },
  comparisonGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
    marginTop: '10px',
  },
  comparisonColumn: {
    backgroundColor: '#F5F0E6',
    padding: '10px',
    borderRadius: '5px',
  },
  comparisonNote: {
    marginTop: '10px',
    fontSize: '12px',
    color: '#666',
  },
  textarea: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    fontFamily: 'inherit',
    fontSize: '14px',
    resize: 'vertical',
  },
  originalCode: {
    backgroundColor: '#d1fae5',
    padding: '2px 6px',
    borderRadius: '3px',
    fontFamily: 'monospace',
    fontSize: '12px',
    display: 'inline-block',
    wordBreak: 'break-all',
  },
  tamperedCode: {
    backgroundColor: '#fee2e2',
    padding: '2px 6px',
    borderRadius: '3px',
    fontFamily: 'monospace',
    fontSize: '12px',
    display: 'inline-block',
    wordBreak: 'break-all',
  },
  noteItem: {
    backgroundColor: '#F5F0E6',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '10px',
  },
  noteText: {
    margin: '0 0 5px 0',
    fontSize: '14px',
  },
  noteMeta: {
    margin: 0,
    fontSize: '11px',
    color: '#666',
  },
  modalActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    marginTop: '20px',
    flexWrap: 'wrap',
  },
  modalRestoreButton: {
    padding: '10px 20px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  modalInvestigateButton: {
    padding: '10px 20px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  modalResolveButton: {
    padding: '10px 20px',
    backgroundColor: '#2E7D32',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  modalCancelButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#666',
    border: '1px solid #666',
    borderRadius: '5px',
    cursor: 'pointer',
  },
};

export default IntegrityAlerts;